'use client';

import { useEffect, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import { RefreshCw, X } from 'lucide-react';
import { isStale, parseVersionText, RELOADED_FOR_KEY, shouldAutoReload } from '@/app/lib/staleClient';

// Version of the bundle this tab is actually running (baked at build time).
const RUNNING_VERSION = process.env.APP_VERSION || '';

// Re-check cadence while the tab stays open, and a throttle so flicking
// between tabs doesn't hammer /version.txt on every visibilitychange.
const CHECK_INTERVAL_MS = 15 * 60 * 1000;
const MIN_CHECK_GAP_MS = 4 * 60 * 1000;

async function fetchDeployedVersion(): Promise<string | null> {
  try {
    const res = await fetch('/version.txt', { cache: 'no-store' });
    if (!res.ok) return null;
    return parseVersionText(await res.text());
  } catch {
    return null;
  }
}

function readReloadedFor(): string | null {
  try {
    return sessionStorage.getItem(RELOADED_FOR_KEY);
  } catch {
    return null;
  }
}

function markReloadedFor(version: string) {
  try {
    sessionStorage.setItem(RELOADED_FOR_KEY, version);
  } catch {
    // sessionStorage unavailable — the reload still happens, we just lose
    // the loop guard for this tab.
  }
}

/**
 * Detects tabs left running an old bundle after a deploy and moves them
 * forward before they crash on a chunk the server no longer has.
 *
 * Three mechanisms, least intrusive first:
 * 1. While stale, same-origin link clicks become full document navigations,
 *    intercepted *before* Next starts the client-side transition — the old
 *    bundle may try to load a route chunk the server no longer has, which
 *    would fail before the pathname ever changes.
 * 2. If a navigation still happens client-side (e.g. programmatic
 *    router.push), the pathname change triggers a hard reload.
 * 3. Tabs that never navigate (dashboards left open) get a calm banner
 *    offering a refresh.
 */
const StaleClientReloader = () => {
  const pathname = usePathname();
  const [deployedVersion, setDeployedVersion] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const lastCheckRef = useRef(0);
  // Pathname at the moment staleness was first seen — reload only fires on
  // a navigation *after* that, never mid-page.
  const stalePathRef = useRef<string | null>(null);

  const stale = isStale(RUNNING_VERSION, deployedVersion);

  useEffect(() => {
    if (!RUNNING_VERSION) return;
    let cancelled = false;

    const check = async () => {
      lastCheckRef.current = Date.now();
      const v = await fetchDeployedVersion();
      if (!cancelled && v) setDeployedVersion(v);
    };

    check();
    const interval = setInterval(check, CHECK_INTERVAL_MS);
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible' && Date.now() - lastCheckRef.current > MIN_CHECK_GAP_MS) {
        check();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  // While stale, convert same-origin link clicks into document navigations.
  // Capture phase on document runs before React's delegated handlers, and
  // Next's <Link> skips its client transition when defaultPrevented is set,
  // so the browser fetches the new page (and bundle) directly instead of
  // the old client trying to load route chunks that may be gone.
  useEffect(() => {
    if (!stale || !deployedVersion) return;
    const onClickCapture = (e: MouseEvent) => {
      if (e.defaultPrevented) return;
      // Leave modified / non-primary clicks (new tab etc.) to the browser.
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const anchor = (e.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
      if (!anchor) return;
      if ((anchor.target && anchor.target !== '_self') || anchor.hasAttribute('download')) return;
      const url = new URL(anchor.href, window.location.href);
      if (url.origin !== window.location.origin) return;
      // Same-page hash jumps don't need a document load.
      if (url.pathname === window.location.pathname && url.search === window.location.search && url.hash) return;
      e.preventDefault();
      e.stopPropagation();
      markReloadedFor(deployedVersion);
      window.location.assign(url.toString());
    };
    document.addEventListener('click', onClickCapture, true);
    return () => document.removeEventListener('click', onClickCapture, true);
  }, [stale, deployedVersion]);

  useEffect(() => {
    if (!stale || !deployedVersion) {
      stalePathRef.current = null;
      return;
    }
    if (stalePathRef.current === null) {
      stalePathRef.current = pathname;
      return;
    }
    if (pathname === stalePathRef.current) return;
    if (!shouldAutoReload(deployedVersion, readReloadedFor())) return;
    markReloadedFor(deployedVersion);
    window.location.reload();
  }, [stale, deployedVersion, pathname]);

  if (!stale || dismissed || !deployedVersion) return null;

  const handleRefresh = () => {
    markReloadedFor(deployedVersion);
    window.location.reload();
  };

  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 max-w-md w-[calc(100%-2rem)]">
      <div className="flex items-center gap-3 rounded-lg border border-indigo-200 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-950 shadow-lg px-4 py-3">
        <RefreshCw className="w-4 h-4 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
        <p className="flex-1 text-sm text-indigo-900 dark:text-indigo-200">
          Powston has been updated. Refresh to load the latest version.
        </p>
        <button
          onClick={handleRefresh}
          className="flex-shrink-0 px-3 py-1.5 text-sm font-medium rounded-md bg-indigo-600 text-white hover:bg-indigo-700 transition-colors"
        >
          Refresh
        </button>
        <button
          onClick={() => setDismissed(true)}
          className="flex-shrink-0 text-indigo-400 hover:text-indigo-600 dark:hover:text-indigo-300"
          aria-label="Dismiss update notice"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default StaleClientReloader;
